import { ArrowRight, Building2, Globe, Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AdviserProfileContent, PublicAdviser } from './api';
import AdviserConversationDialog from './AdviserConversationDialog';
import './Advisers.css';

export function safeWebUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}
function ExternalLink({ url, children }: { url?: string; children: React.ReactNode }) {
  const href = safeWebUrl(url);
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : null;
}
export function AdviserCard({ adviser }: { adviser: PublicAdviser }) {
  return (
    <article className="adviser-card">
      <div
        className="adviser-card-cover"
        style={
          safeWebUrl(adviser.coverImageUrl)
            ? { backgroundImage: `url("${safeWebUrl(adviser.coverImageUrl)}")` }
            : undefined
        }
      />
      <div className="adviser-card-body">
        <div className="adviser-logo">
          {safeWebUrl(adviser.logoUrl) ? (
            <img
              src={safeWebUrl(adviser.logoUrl)}
              alt={`${adviser.firmName} logo`}
              loading="lazy"
            />
          ) : (
            <Building2 aria-hidden="true" />
          )}
        </div>
        <h3>
          <Link to={`/advisers/${adviser.slug}`}>{adviser.firmName}</Link>
        </h3>
        <p className="adviser-card-headline">{adviser.headline}</p>
        {adviser.location && (
          <p className="adviser-location">
            <MapPin size={15} aria-hidden="true" />
            {adviser.location}
          </p>
        )}
        <div className="adviser-tags">
          {adviser.specialities?.slice(0, 3).map((value) => (
            <span key={value}>{value}</span>
          ))}
        </div>
        <Link className="adviser-text-link" to={`/advisers/${adviser.slug}`}>
          Meet the firm <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}
export default function AdviserProfileView({
  profile,
  preview = false,
  slug,
}: {
  profile: AdviserProfileContent;
  preview?: boolean;
  slug?: string;
}) {
  return (
    <div className="adviser-profile-view">
      {preview && (
        <div className="adviser-notice">
          Private preview · only published information appears in the directory.
        </div>
      )}
      <div
        className="adviser-profile-cover"
        style={
          safeWebUrl(profile.coverImageUrl)
            ? { backgroundImage: `url("${safeWebUrl(profile.coverImageUrl)}")` }
            : undefined
        }
      />
      <div className="adviser-profile-intro">
        <div className="adviser-logo adviser-logo-large">
          {safeWebUrl(profile.logoUrl) ? (
            <img src={safeWebUrl(profile.logoUrl)} alt={`${profile.firmName} logo`} />
          ) : (
            <Building2 size={40} aria-hidden="true" />
          )}
        </div>
        <div className="adviser-profile-title">
          <p className="adviser-eyebrow">Strategic advisory</p>
          <h1>{profile.firmName || 'Your firm'}</h1>
          <p>{profile.headline}</p>
        </div>
        <div className="adviser-profile-actions">
          <AdviserConversationDialog
            slug={slug || (profile as PublicAdviser).slug}
            firmName={profile.firmName || 'Your firm'}
            preview={preview}
          />
        </div>
      </div>
      <div className="adviser-profile-grid">
        <div>
          <section className="adviser-panel">
            <h2>About the firm</h2>
            <p className="adviser-preserve-lines">
              {profile.introduction || 'Add an introduction to your firm.'}
            </p>
          </section>
          {profile.services?.length > 0 && (
            <section className="adviser-panel">
              <h2>How we can help</h2>
              <div className="adviser-service-grid">
                {profile.services.map((service) => (
                  <div key={service}>
                    <h3>{service}</h3>
                  </div>
                ))}
              </div>
            </section>
          )}
          {profile.team?.length > 0 && (
            <section className="adviser-panel">
              <h2>Meet the team</h2>
              <div className="adviser-team-grid">
                {profile.team.map((member, index) => (
                  <article className="adviser-team-person" key={member.id || index}>
                    {safeWebUrl(member.photoUrl) ? (
                      <img src={safeWebUrl(member.photoUrl)} alt={member.name} loading="lazy" />
                    ) : (
                      <div className="adviser-initial">{member.name.slice(0, 1)}</div>
                    )}
                    <div>
                      <h3>{member.name}</h3>
                      <p className="adviser-muted">{member.role}</p>
                      <p className="adviser-preserve-lines">{member.biography}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>
        <aside>
          <section className="adviser-panel">
            <h2>Firm details</h2>
            {profile.location && (
              <p className="adviser-location">
                <MapPin size={17} aria-hidden="true" />
                {profile.location}
              </p>
            )}
            {profile.serviceAreas?.length > 0 && (
              <>
                <h3>Service areas</h3>
                <p>{profile.serviceAreas.join(' · ')}</p>
              </>
            )}
            <div className="adviser-contact-links">
              <ExternalLink url={profile.websiteUrl}>
                <Globe size={16} />
                Website
              </ExternalLink>
              <ExternalLink url={profile.linkedinUrl}>
                <Linkedin size={16} />
                LinkedIn
              </ExternalLink>
              {profile.contactEmail && (
                <a href={`mailto:${profile.contactEmail}`}>
                  <Mail size={16} />
                  {profile.contactEmail}
                </a>
              )}
              {profile.contactPhone && (
                <a href={`tel:${profile.contactPhone.replace(/[^+\d]/g, '')}`}>
                  <Phone size={16} />
                  {profile.contactPhone}
                </a>
              )}
            </div>
          </section>
          {profile.specialities?.length > 0 && (
            <section className="adviser-panel">
              <h2>Expertise</h2>
              <div className="adviser-tags">
                {profile.specialities.map((value) => (
                  <span key={value}>{value}</span>
                ))}
              </div>
            </section>
          )}
          {profile.industries?.length > 0 && (
            <section className="adviser-panel">
              <h2>Industries</h2>
              <div className="adviser-tags">
                {profile.industries.map((value) => (
                  <span key={value}>{value}</span>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
